/**
 * InstallBanner — Premium Apple-style PWA install prompt.
 *
 * Behavior:
 *   • Mobile-only (<768px). Hidden on desktop entirely.
 *   • Hidden when already running as a standalone PWA.
 *   • Shows Android/Chrome flow when `beforeinstallprompt` fires.
 *   • Shows iOS/Safari flow with "Tap Share → Add to Home" instructions
 *     when on iOS (no native install API exists).
 *   • Once dismissed (X), it is NEVER shown again to that user/device
 *     (persisted via localStorage). Once successfully installed, ditto.
 *
 * Visual:
 *   Black translucent (rgba) + 18px backdrop-blur, soft gold accent
 *   (#E8C07D), rounded 22px, subtle gold border, rises from bottom on
 *   first appearance with a 700ms ease-out, fades out on dismiss.
 *
 * Self-contained: no external deps beyond React. Drops into App.js once.
 */
import { useEffect, useState, useCallback } from "react";

const DISMISS_KEY = "os_pwa_install_dismissed_at";
const FIRST_VISIT_KEY = "os_pwa_first_visit_at";

// Wait this long before showing the banner — gives the user a moment to
// take in the app first, never feels intrusive on the very first second.
const SHOW_AFTER_MS = 8000;

// After dismissing the banner with the X, hide it for this long. We do NOT
// hide forever, because users sometimes uninstall and want to reinstall, or
// change their mind. 7 days is calm enough to feel non-pushy but recovers.
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

// One-time cleanup of legacy localStorage keys from earlier versions of the
// banner that persisted "dismissed forever" or "installed forever". If we
// find them, drop them so users naturally re-enter the install flow after
// uninstalling. Idempotent: runs once per page load, no-op if already clean.
function migrateLegacyKeys() {
    try {
        const legacy = ["os_pwa_install_dismissed", "os_pwa_installed"];
        legacy.forEach((k) => {
            if (localStorage.getItem(k) !== null) localStorage.removeItem(k);
        });
    } catch { /* noop */ }
}

function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

function isMobile() {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
}

function isIOS() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    // Modern iPad reports "Macintosh" — disambiguate via touch.
    const iPadOS = ua.includes("Macintosh") && navigator.maxTouchPoints > 1;
    return /iphone|ipod|ipad/i.test(ua) || iPadOS;
}

function recentlyDismissed() {
    try {
        const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
        if (!ts) return false;
        return Date.now() - ts < DISMISS_COOLDOWN_MS;
    } catch { return false; }
}

export default function InstallBanner() {
    const [visible, setVisible] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [platform, setPlatform] = useState("android"); // "ios" | "android"
    const [closing, setClosing] = useState(false);

    // ── Decide whether we should ever show the banner this session.
    useEffect(() => {
        if (typeof window === "undefined") return;
        // First: clean up legacy "forever" keys from previous deploy
        migrateLegacyKeys();
        if (!isMobile()) return;
        if (isStandalone()) return;
        // 7-day soft cooldown after explicit X dismissal (not forever).
        if (recentlyDismissed()) return;

        // Mark first-visit timestamp to throttle.
        try {
            if (!localStorage.getItem(FIRST_VISIT_KEY)) {
                localStorage.setItem(FIRST_VISIT_KEY, String(Date.now()));
            }
        } catch { /* noop */ }

        setPlatform(isIOS() ? "ios" : "android");

        // Capture native install prompt on Android/Chrome.
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);

        // App got installed via native flow — hide for this session only.
        // Do NOT persist; if the user uninstalls and reopens the site in
        // Chrome (no longer standalone), the banner naturally shows again.
        const onInstalled = () => {
            setVisible(false);
        };
        window.addEventListener("appinstalled", onInstalled);

        // Show after a calm delay.
        const t = setTimeout(() => {
            setVisible(true);
        }, SHOW_AFTER_MS);

        return () => {
            clearTimeout(t);
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const handleInstall = useCallback(async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        try {
            await deferredPrompt.userChoice;
        } catch { /* noop */ }
        setDeferredPrompt(null);
        setVisible(false);
    }, [deferredPrompt]);

    const handleDismiss = useCallback(() => {
        setClosing(true);
        // Soft cooldown: store dismissal timestamp, banner will return
        // automatically after DISMISS_COOLDOWN_MS (7 days).
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
        setTimeout(() => setVisible(false), 280);
    }, []);

    if (!visible) return null;

    // Android side: show install button only when we have a prompt.
    // If we don't have a prompt (some Chrome flavors delay it) we still show
    // instructions to add to home, since the user can always do it manually.
    const canInstallNative = platform === "android" && !!deferredPrompt;

    return (
        <>
            <style>{`
                @keyframes os-pwa-rise {
                    from { transform: translateY(140%); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
                @keyframes os-pwa-fall {
                    from { transform: translateY(0);    opacity: 1; }
                    to   { transform: translateY(140%); opacity: 0; }
                }
                .os-pwa-banner {
                    position: fixed;
                    left: 12px; right: 12px;
                    bottom: max(12px, env(safe-area-inset-bottom, 12px));
                    z-index: 9998;
                    border-radius: 22px;
                    padding: 18px 20px 20px 20px;
                    background: rgba(8, 6, 5, 0.78);
                    -webkit-backdrop-filter: blur(22px) saturate(140%);
                    backdrop-filter: blur(22px) saturate(140%);
                    border: 1px solid rgba(232, 192, 125, 0.22);
                    box-shadow: 0 18px 48px rgba(0,0,0,0.55), 0 0 0 0.5px rgba(232,192,125,0.10) inset;
                    color: #f5ecdb;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    animation: os-pwa-rise 700ms cubic-bezier(.2,.8,.2,1) both;
                }
                .os-pwa-banner.os-pwa-closing { animation: os-pwa-fall 280ms ease-in both; }
                .os-pwa-close {
                    position: absolute; top: 10px; right: 12px;
                    width: 26px; height: 26px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: rgba(255,255,255,0.06);
                    border: 1px solid rgba(232,192,125,0.18);
                    border-radius: 50%;
                    color: #f5ecdb;
                    cursor: pointer;
                    transition: background 180ms ease, transform 180ms ease;
                }
                .os-pwa-close:hover, .os-pwa-close:active { background: rgba(232,192,125,0.16); transform: scale(0.95); }
                .os-pwa-row { display: flex; gap: 14px; align-items: flex-start; }
                .os-pwa-icon {
                    width: 46px; height: 46px;
                    border-radius: 11px;
                    background: #000;
                    border: 1px solid rgba(232,192,125,0.32);
                    flex: 0 0 auto;
                    display: flex; align-items: center; justify-content: center;
                    overflow: hidden;
                    box-shadow: 0 0 0 0.5px rgba(232,192,125,0.18) inset;
                }
                .os-pwa-icon img { width: 80%; height: 80%; object-fit: contain; }
                .os-pwa-text { flex: 1; min-width: 0; padding-right: 28px; }
                .os-pwa-title {
                    font-family: Georgia, "Cormorant Garamond", serif;
                    font-weight: 500;
                    font-size: 17px;
                    letter-spacing: 0.01em;
                    color: #f3e3c2;
                    margin: 0 0 3px 0;
                    line-height: 1.25;
                }
                .os-pwa-sub {
                    font-size: 13px;
                    line-height: 1.45;
                    color: rgba(245, 236, 219, 0.72);
                    margin: 0;
                }
                .os-pwa-cta-row {
                    margin-top: 14px;
                    display: flex; gap: 10px; align-items: center;
                }
                .os-pwa-cta {
                    appearance: none;
                    border: none;
                    background: linear-gradient(180deg, #f0d9a8 0%, #d6b07a 100%);
                    color: #1a120a;
                    font-weight: 600;
                    font-size: 14px;
                    letter-spacing: 0.01em;
                    padding: 10px 18px;
                    border-radius: 12px;
                    cursor: pointer;
                    box-shadow: 0 6px 18px rgba(214,176,122,0.25);
                    transition: transform 140ms ease, box-shadow 140ms ease;
                }
                .os-pwa-cta:hover, .os-pwa-cta:active {
                    transform: translateY(-1px);
                    box-shadow: 0 9px 24px rgba(214,176,122,0.32);
                }
                .os-pwa-ios-hint {
                    margin-top: 12px;
                    font-size: 13px;
                    line-height: 1.55;
                    color: rgba(245, 236, 219, 0.78);
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(232,192,125,0.14);
                    border-radius: 12px;
                    padding: 10px 12px;
                    display: flex; gap: 8px; align-items: center;
                }
                .os-pwa-share-icon {
                    width: 18px; height: 18px; flex: 0 0 auto;
                    color: #E8C07D;
                }
                @media (min-width: 769px) {
                    .os-pwa-banner { display: none !important; }
                }
            `}</style>
            <div
                className={`os-pwa-banner${closing ? " os-pwa-closing" : ""}`}
                role="dialog"
                aria-labelledby="os-pwa-title"
                aria-describedby="os-pwa-sub"
                data-testid="pwa-install-banner"
            >
                <button
                    type="button"
                    aria-label="Chiudi"
                    className="os-pwa-close"
                    data-testid="pwa-install-close"
                    onClick={handleDismiss}
                >
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                        <path d="M2 2 L10 10 M10 2 L2 10" />
                    </svg>
                </button>

                <div className="os-pwa-row">
                    <div className="os-pwa-icon">
                        <img src="/brand/pwa-192.png" alt="" aria-hidden="true" />
                    </div>
                    <div className="os-pwa-text">
                        <h3 id="os-pwa-title" className="os-pwa-title">
                            Installa Other Side
                        </h3>
                        <p id="os-pwa-sub" className="os-pwa-sub">
                            Aggiungila alla schermata Home e usala come una vera app.
                        </p>
                    </div>
                </div>

                {platform === "ios" && (
                    <div className="os-pwa-ios-hint" data-testid="pwa-install-ios-hint">
                        <svg className="os-pwa-share-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 3v12" />
                            <path d="M8 7l4-4 4 4" />
                            <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
                        </svg>
                        <span>Tocca <strong style={{ color: "#f3e3c2" }}>Condividi</strong> → <strong style={{ color: "#f3e3c2" }}>Aggiungi a Home</strong>.</span>
                    </div>
                )}

                {canInstallNative && (
                    <div className="os-pwa-cta-row">
                        <button
                            type="button"
                            className="os-pwa-cta"
                            onClick={handleInstall}
                            data-testid="pwa-install-cta"
                        >
                            Installa app
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
